


/**
 * Compact widget summary for AI context
 * Contains only essential information to minimize token usage
 */
// export interface WidgetSummary {
//   id;
//   title;
//   type;
//   description;
//   position?: {
//     x;
//     y;
//     row; // Calculated row number (approximate)
//     col; // Calculated column number (approximate)
//   };
// }

/**
 * Generate a compact summary of a widget for AI context
 * Includes only essential information to minimize token usage
 */
export function generateWidgetSummary(
  widget,
  layout?: GridItem
): WidgetSummary {
  const description = generateWidgetDescription(widget);
  
  const summary: WidgetSummary = {
    id: widget.id,
    title: widget.title || `Untitled ${widget.type}`,
    type: widget.type,
    description,
  };

  // Add position information if layout is provided
  if (layout) {
    // Calculate approximate row and column (assuming 12-column grid)
    const row = Math.floor(layout.y / 5); // Approximate, assuming avg height ~5
    const col = layout.x;
    
    summary.position = {
      x: layout.x,
      y: layout.y,
      row: row + 1, // 1-indexed for user-friendly display
      col: col + 1, // 1-indexed for user-friendly display
    };
  }

  return summary;
}

/**
 * Generate a human-readable description of a widget
 * Based on its type and configuration
 */
function generateWidgetDescription(widget) {
  switch (widget.type) {
    case 'metric': {
      const config = widget.config;
      const metricType = config?.metricType || 'unknown';
      const enabledSources = widget.dataSources.filter((ds) => ds.enabled);
      
      if (enabledSources.length > 0) {
        const sourceLabels = enabledSources.map((ds) => ds.label).join(', ');
        return `Metric (${metricType}): ${sourceLabels}`;
      }
      
      return `Metric (${metricType}): No data sources configured`;
    }

    case 'line-chart':
    case 'bar-chart': {
      const config = widget.config;
      const chartType = config?.chartType || widget.type.replace('-chart', '');
      const enabledSources = widget.dataSources.filter((ds) => ds.enabled);
      
      if (enabledSources.length > 0) {
        const sourceLabels = enabledSources.map((ds) => ds.label).join(', ');
        return `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart: ${sourceLabels}`;
      }
      
      return `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart: No data sources`;
    }

    case 'table': {
      const config = widget.config;
      const columnConfigs = config?.columnConfigs || [];
      const columnCount = columnConfigs.length;
      
      if (columnCount > 0) {
        const columnLabels = columnConfigs.slice(0, 3).map((col) => col.label).join(', ');
        const moreColumns = columnCount > 3 ? ` (+${columnCount - 3} more)` : '';
        return `Table: ${columnLabels}${moreColumns}`;
      }
      
      return 'Table: No columns configured';
    }

    case 'insight': {
      const config = widget.config;
      const condition = config?.condition;
      const type = config?.type || 'info';
      
      if (condition) {
        const metric = condition.metric || 'unknown';
        const operator = condition.operator || 'unknown';
        const value = condition.value || 'unknown';
        return `Insight (${type}): ${metric} ${operator} ${value}`;
      }
      
      return `Insight (${type}): Condition not configured`;
    }

    default:
      return `Widget (${widget.type})`;
  }
}

/**
 * Generate summaries for multiple widgets
 * Useful for passing widget context to AI
 */
export function generateWidgetSummaries(
  widgets,
  layouts
) {
  const summaries = [];
  
  // Create a map of widget IDs to layouts for quick lookup
  const layoutMap = new Map();
  layouts.forEach((layout) => {
    layoutMap.set(layout.i, layout);
  });

  Object.values(widgets).forEach((widget) => {
    const layout = layoutMap.get(widget.id);
    summaries.push(generateWidgetSummary(widget, layout));
  });

  // Sort by position (y then x) for consistent ordering
  summaries.sort((a, b) => {
    if (a.position && b.position) {
      if (a.position.y !== b.position.y) {
        return a.position.y - b.position.y;
      }
      return a.position.x - b.position.x;
    }
    // If no position, sort by title
    return a.title.localeCompare(b.title);
  });

  return summaries;
}

